import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  PersonalInfoStep, SummaryStep, ExperienceStep, EducationStep, SkillsStep, ProjectsStep, ReferencesStep, ReviewStep,
} from "../components/builder/Steps";
import Preview from "../components/builder/Preview";
import TemplatePicker from "../components/builder/TemplatePicker";
import { exportToPDF, exportToDOCX, exportReferencesToPDF, exportReferencesToDOCX } from "../lib/exporters";
import ShareDialog from "../components/ShareDialog";
import { toast } from "sonner";
import {
  Loader2, Save, Download, Check, ArrowLeft, ArrowRight, Palette, FileText, FileType2, Lock, Eye, Share2,
} from "lucide-react";

const STEPS = [
  { id: "personal", label: "Personal", Component: PersonalInfoStep },
  { id: "summary", label: "Summary", Component: SummaryStep },
  { id: "experience", label: "Experience", Component: ExperienceStep },
  { id: "education", label: "Education", Component: EducationStep },
  { id: "skills", label: "Skills", Component: SkillsStep },
  { id: "projects", label: "Projects", Component: ProjectsStep },
  { id: "references", label: "References", Component: ReferencesStep },
  { id: "review", label: "Review", Component: ReviewStep },
];

export default function Builder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [resume, setResume] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const saveTimer = useRef(null);

  // load
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/resumes/${id}`);
        // Normalize missing fields
        setResume({
          ...data,
          accentColor: data.accentColor || "#0f172a",
          showReferences: !!data.showReferences,
          personalInfo: data.personalInfo || {},
          experience: data.experience || [],
          education: data.education || [],
          skills: data.skills || [],
          projects: data.projects || [],
          references: data.references || [],
          summary: data.summary || "",
        });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        nav("/dashboard");
      }
    })();
  }, [id, nav]);

  // autosave (debounced) when resume changes
  const persist = useCallback(async (r) => {
    if (!r) return;
    setSaving(true);
    try {
      const payload = {
        title: r.title,
        template: r.template,
        accentColor: r.accentColor || "#0f172a",
        showReferences: !!r.showReferences,
        personalInfo: r.personalInfo || {},
        summary: r.summary || "",
        experience: r.experience || [],
        education: r.education || [],
        skills: r.skills || [],
        projects: r.projects || [],
        references: r.references || [],
      };
      await api.put(`/resumes/${id}`, payload);
      setLastSaved(new Date());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }, [id]);

  const update = (next) => {
    setResume(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 900);
  };

  const saveNow = async () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    await persist(resume);
    toast.success("Saved");
  };

  const downloadPDF = async () => {
    setExporting(true);
    try {
      // Ensure latest is saved before exporting
      await persist(resume);
      // Temporarily render at 1x for cleaner export
      const previewEl = document.getElementById("resume-export");
      const prevTransform = previewEl.style.transform;
      previewEl.style.transform = "scale(1)";
      await new Promise((r) => setTimeout(r, 100));
      await exportToPDF("resume-export", `${resume.title || "resume"}.pdf`);
      previewEl.style.transform = prevTransform;
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadDOCX = async () => {
    setExporting(true);
    try {
      await persist(resume);
      await exportToDOCX(resume, `${resume.title || "resume"}.docx`);
      toast.success("DOCX downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setExporting(false);
    }
  };

  if (!resume) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const Step = STEPS[stepIdx].Component;
  const progress = ((stepIdx + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Top bar */}
      <div className="border-b border-slate-200 bg-white sticky top-16 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="sm" onClick={() => nav("/dashboard")} className="h-9" data-testid="builder-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Input
              value={resume.title}
              onChange={(e) => update({ ...resume, title: e.target.value })}
              className="max-w-sm font-display font-semibold border-transparent hover:border-slate-200 focus-visible:border-slate-300"
              data-testid="builder-title-input"
            />
            <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500" data-testid="save-indicator">
              {saving ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              ) : lastSaved ? (
                <><Check className="w-3 h-3 text-emerald-500" /> Saved {lastSaved.toLocaleTimeString()}</>
              ) : (
                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Encrypted</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="h-9" data-testid="builder-template-btn">
                  <Palette className="w-4 h-4 mr-1.5" /> Style
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Customize</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <TemplatePicker
                    value={resume.template}
                    onChange={(t) => update({ ...resume, template: t })}
                    accentColor={resume.accentColor || "#0f172a"}
                    onAccentChange={(c) => update({ ...resume, accentColor: c })}
                  />
                </div>
              </SheetContent>
            </Sheet>
            <Button variant="outline" size="sm" onClick={saveNow} className="h-9" data-testid="builder-save-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="h-9" data-testid="builder-share-btn">
              <Share2 className="w-4 h-4 mr-1.5" /> Share
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 h-9" disabled={exporting} data-testid="builder-download-btn">
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> Download</>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={downloadPDF} data-testid="download-pdf-option">
                  <FileText className="w-4 h-4 mr-2" /> Download resume (PDF)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadDOCX} data-testid="download-docx-option">
                  <FileType2 className="w-4 h-4 mr-2" /> Download resume (DOCX)
                </DropdownMenuItem>
                {(resume.references || []).length > 0 && (
                  <>
                    <div className="my-1 border-t border-slate-100" />
                    <DropdownMenuItem
                      onClick={async () => {
                        setExporting(true);
                        try {
                          await persist(resume);
                          await exportReferencesToPDF(resume, `${resume.title || "resume"} — references.pdf`);
                          toast.success("References PDF downloaded");
                        } catch (e) { toast.error(e.message); }
                        finally { setExporting(false); }
                      }}
                      data-testid="download-refs-pdf-option"
                    >
                      <FileText className="w-4 h-4 mr-2" /> References sheet (PDF)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        setExporting(true);
                        try {
                          await persist(resume);
                          await exportReferencesToDOCX(resume, `${resume.title || "resume"} — references.docx`);
                          toast.success("References DOCX downloaded");
                        } catch (e) { toast.error(e.message); }
                        finally { setExporting(false); }
                      }}
                      data-testid="download-refs-docx-option"
                    >
                      <FileType2 className="w-4 h-4 mr-2" /> References sheet (DOCX)
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stepper */}
        <div className="max-w-[1600px] mx-auto px-6 pb-4">
          <div className="flex items-center justify-between text-xs">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setStepIdx(i)}
                className={`flex items-center gap-2 ${i === stepIdx ? "text-blue-600 font-semibold" : i < stepIdx ? "text-slate-700" : "text-slate-400"}`}
                data-testid={`step-tab-${s.id}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-mono-display ${i === stepIdx ? "bg-blue-600 text-white" : i < stepIdx ? "bg-slate-200 text-slate-700" : "bg-slate-100 text-slate-500"}`}>
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            ))}
          </div>
          <div className="stepper-line mt-2" style={{ "--progress": `${progress}%` }} />
        </div>
      </div>

      {/* Split view */}
      <div className="max-w-[1600px] mx-auto px-6 py-6 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 xl:col-span-5">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <Step resume={resume} update={update} />
            <div className="mt-8 flex justify-between">
              <Button variant="outline" onClick={() => setStepIdx(Math.max(0, stepIdx - 1))} disabled={stepIdx === 0} data-testid="builder-prev-btn">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              {stepIdx < STEPS.length - 1 ? (
                <Button onClick={() => setStepIdx(stepIdx + 1)} className="bg-slate-900 hover:bg-slate-800" data-testid="builder-next-btn">
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button onClick={downloadPDF} className="bg-blue-600 hover:bg-blue-700" data-testid="builder-finish-download-btn">
                  <Download className="w-4 h-4 mr-1.5" /> Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 xl:col-span-7">
          <div className="lg:sticky lg:top-[180px]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-display flex items-center gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Live preview
              </div>
              <span className="text-xs text-slate-400">
                {({ centered: "Default", modern: "Modern", classic: "Traditional" })[resume.template] || "Default"} template
              </span>
            </div>
            <Preview resume={resume} />
          </div>
        </div>
      </div>

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} resumeId={id} />
    </div>
  );
}
